import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { components } from "@/lib/openapi";
import {
  useBaggageHalls,
  useCreateBaggageCarousel,
  useOAGBaggageCounters,
} from "@/queries/baggage";
import { getCarriers } from "@/queries/landing";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type BaggageCarouselCreateSchema =
  components["schemas"]["BaggageCarouselCreateSchema"];

const baggageCarouselFormSchema = z.object({
  name: z.string().min(1, "Carousel name is required"),
  baggage_hall_id: z.number().min(1, "Baggage hall is required"),
  identifiers: z.array(z.string()).default([]),
  preferred_carriers: z.array(z.string()).default([]),
});

type BaggageCarouselFormValues = z.infer<typeof baggageCarouselFormSchema>;

export function CreateBaggageCarouselButton() {
  const [open, setOpen] = useState(false);

  const { data: baggageHalls, isLoading: loadingHalls } = useBaggageHalls();
  const { data: carriers, isLoading: loadingCarriers } = getCarriers();
  const { data: oagCounters, isLoading: loadingOAGCounters } =
    useOAGBaggageCounters();
  const createMutation = useCreateBaggageCarousel();

  const carrierOptions = useMemo(() => {
    if (!carriers) return [];
    return carriers.map((carrier) => ({
      value: carrier.code,
      label: `${carrier.name} (${carrier.code})`,
    }));
  }, [carriers]);

  const identifierOptions = useMemo(() => {
    if (!oagCounters) return [];
    return oagCounters.map((counter) => ({
      value: counter,
      label: counter,
    }));
  }, [oagCounters]);

  const form = useForm<BaggageCarouselFormValues>({
    resolver: zodResolver(baggageCarouselFormSchema),
    defaultValues: {
      name: undefined,
      baggage_hall_id: undefined,
      identifiers: [],
      preferred_carriers: [],
    },
  });

  const handleSubmit = (values: BaggageCarouselFormValues) => {
    createMutation.mutate(values as BaggageCarouselCreateSchema, {
      onSuccess: () => {
        form.reset();
        setOpen(false);
      },
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      createMutation.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Carousel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create Baggage Carousel</DialogTitle>
          <DialogDescription>
            Add a new baggage carousel to a baggage hall.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Carousel 1"
                      {...field}
                      value={field.value || ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="baggage_hall_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Baggage Hall</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            loadingHalls ? "Loading..." : "Select baggage hall"
                          }
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {baggageHalls?.map((hall) => (
                        <SelectItem key={hall.id} value={hall.id.toString()}>
                          {hall.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="identifiers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>OAG Identifiers</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={identifierOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select OAG identifiers"
                      disabled={loadingOAGCounters}
                      creatable
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="preferred_carriers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Preferred Carriers (Optional)</FormLabel>
                  <FormControl>
                    <MultiSelect
                      options={carrierOptions}
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Select carriers"
                      disabled={loadingCarriers}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {createMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span>
                  {createMutation.error?.message ||
                    "Failed to create baggage carousel. Please try again."}
                </span>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Carousel"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
